/*  MUI  */
import { ExpandMore } from '@mui/icons-material';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material';

/*  Shared  */
import { MultiInput } from 'application/Shared/Components/Inputs';
import {
    LifecyclePicker,
    RiskPicker,
    TagsPicker,
    TenantPicker
} from 'application/Shared/Components/Pickers';
import { TooltipQ } from 'application/Shared/Components/Tooltips';
import { Helpers, Notify, Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'router/withRouter';

const renderEqualOperand = () => (
    <Select
        IconComponent={ExpandMore}
        variant="standard"
        value="equal"
        style={{
            fontWeight: '600',
            textAlign: 'center',
            width: '100%'
        }}
    >
        <MenuItem key="equal" value="equal">
            =
        </MenuItem>
    </Select>
);

class Create extends Component {
    constructor(props) {
        super(props);
        this.state = {
            clientDefault: false,
            clientId: '0',
            conditions: {},
            destinations: [],
            event: '',
            loaded: false,
            method: ''
        };
    }

    componentDidMount() {
        this.load();
    }

    load = () => {
        const { defaults } = this.props;
        const { event, method } = defaults;

        this.setState({ destinations: [], event, loaded: true, method });
        this.setEventConditions(event);
    };

    handleClientDefault = (clientDefault) => {
        if (clientDefault) {
            this.setState({ clientId: '0' });
        }
        this.setState({ clientDefault });
    };

    handleEvent = (event) => {
        const { config } = this.props;
        const { clientDefault } = this.state;

        if (!event) {
            return;
        }

        const eventConfig = config[event];
        if (!eventConfig.clientDefault && clientDefault) {
            this.setState({ clientDefault: false, clientId: '0' });
        }

        this.setState({ event });
        this.setEventConditions(event);
    };

    handleConditionField = (key, field, value) => {
        const { conditions } = this.state;
        const condition = conditions[key];

        this.setState({
            conditions: {
                ...conditions,
                [key]: { ...condition, [field]: value }
            }
        });
    };

    handleNumberField = (key, field, value) => {
        const { conditions } = this.state;
        const condition = conditions[key];
        const { number } = condition;

        this.setState({
            conditions: {
                ...conditions,
                [key]: {
                    ...condition,
                    number: { ...number, [field]: value }
                }
            }
        });
    };

    handleMethod = (method) => {
        if (!method) {
            return;
        }
        this.setState({ method });
    };

    setEventConditions = (event) => {
        const { config } = this.props;

        if (!Helpers.isset(config, event, 'conditions')) {
            this.setState({ conditions: {} });
            return;
        }

        const { conditions } = config[event];
        this.setState({ conditions });
    };

    save = async () => {
        const { t, match, partner, handleChangeView } = this.props;
        const { clientId: clientIdFromUrl } = match.params;
        const {
            event,
            method,
            destinations,
            conditions,
            clientId,
            clientDefault
        } = this.state;

        const payload = this.validate({
            clientDefault,
            clientId,
            conditions,
            destinations,
            event,
            method,
            partner
        });
        if (!payload) {
            return;
        }

        if (clientIdFromUrl) {
            payload.platformTenantId = clientIdFromUrl;
        }

        try {
            const response = await axios.post('/workflowConfig', payload);
            if (!Response.hasResponse(response)) {
                debug.e('Unable to add alert:', response);
                Notify.error(t('Common.error'));
                return;
            }

            Notify.success(t('Alert.create_success'));
            handleChangeView('configurations');
        } catch (e) {
            debug.e('Error adding alert:', e);
            Notify.error(t('Common.error'));
        }
    };

    validate = (data) => {
        const { t, methods, partner, config } = this.props;
        const { event, method, conditions, clientId, clientDefault } = data;
        let { destinations } = data;

        if (destinations.length === 0) {
            Notify.error(t('Alert.error_no_destinations'));
            return false;
        }

        const eventConfig = config[event];
        if (partner && !eventConfig.clientDefault && clientId === '0') {
            Notify.error(t('You must Choose a Client'));
            return false;
        }

        let valid = true;
        destinations = destinations.map((val) => {
            const { destination } = val;
            if (method === 'email' && !Helpers.isEmail(destination)) {
                valid = false;
                Notify.error(
                    `Destination '${destination}' is an Invalid ${methods[method]}.`
                );
                return '';
            }
            return destination;
        });

        if (!valid) {
            return false;
        }

        const payload = {
            conditions,
            destinations,
            event,
            method
        };

        if (!partner) {
            return payload;
        }

        if (clientId === '0') {
            return {
                ...payload,
                clientDefault,
                managedByPartner: true
            };
        }

        return {
            ...payload,
            clientId,
            managedByPartner: true
        };
    };

    renderClientSelect = () => {
        const { t, config } = this.props;
        const { clientId, clientDefault, event } = this.state;

        let eventConfig = {};
        if (Helpers.isset(config, event)) {
            eventConfig = config[event];
        }

        let allValue = t('Common.choose_a_client');
        if (clientDefault) {
            allValue = t('Common.client_default');
        }

        return (
            <Grid item xs={12}>
                <Typography color="textSecondary" fontWeight={600}>
                    {t('Common.client')}
                </Typography>
                <TenantPicker
                    disabled={clientDefault}
                    allValue={allValue}
                    allValueId="0"
                    value={clientId}
                    onChange={(v) => this.setState({ clientId: v })}
                />
                {eventConfig.clientDefault && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={clientDefault}
                                onChange={(e) =>
                                    this.handleClientDefault(e.target.checked)
                                }
                            />
                        }
                        label={
                            <Box display="inline-flex" alignItems="center">
                                {t('Alert.set_as_default')}
                                <TooltipQ
                                    text={t('Tooltips.alert.set_as_default')}
                                />
                            </Box>
                        }
                        style={{ marginBottom: '-10px', paddingLeft: '2px' }}
                    />
                )}
            </Grid>
        );
    };

    renderEventSelect = () => {
        const { t, events, config } = this.props;
        const { event } = this.state;

        return (
            <Grid item xs={12}>
                <Typography color="textSecondary" fontWeight={600}>
                    {t('Alert.event')}
                </Typography>
                <Select
                    IconComponent={ExpandMore}
                    variant="standard"
                    value={event}
                    onChange={(e) => this.handleEvent(e.target.value)}
                    style={{ width: '100%' }}
                >
                    {events.map((key) => {
                        const eventConfig = config[key];
                        const { alert, name, comingSoon } = eventConfig;

                        if (!alert && !comingSoon) {
                            return null;
                        }

                        let value = key;
                        let text = name;

                        if (comingSoon) {
                            value = false;
                            text = (
                                <Box
                                    sx={{
                                        color: 'text.disabled',
                                        cursor: 'not-allowed'
                                    }}
                                >
                                    {name} (Coming Soon)
                                </Box>
                            );
                        }

                        return (
                            <MenuItem key={key} value={value}>
                                {text}
                            </MenuItem>
                        );
                    })}
                </Select>
            </Grid>
        );
    };

    renderOperands = (key) => {
        const { operands } = this.props;
        const { conditions } = this.state;
        const condition = conditions[key];

        const { number } = condition;
        if (!number) {
            return null;
        }

        let { operand } = number;
        if (!operand) {
            operand = 'equal';
        }

        return (
            <Select
                IconComponent={ExpandMore}
                variant="standard"
                value={operand}
                onChange={(e) =>
                    this.handleNumberField(key, 'operand', e.target.value)
                }
                style={{
                    fontWeight: '600',
                    textAlign: 'center',
                    width: '100%'
                }}
            >
                {Object.keys(operands).map((op) => (
                    <MenuItem key={op} value={op}>
                        {operands[op]}
                    </MenuItem>
                ))}
            </Select>
        );
    };

    renderNumberCondition = (key) => {
        const { conditions } = this.state;
        const condition = conditions[key];
        const { value, number } = condition;

        let { min, max } = number;
        min = min || 0;
        max = max || 9999;

        return (
            <>
                <Grid item xs={2}>
                    {this.renderOperands(key)}
                </Grid>
                <Grid item xs={5}>
                    <TextField
                        type="number"
                        fullWidth
                        value={value}
                        onChange={(e) =>
                            this.handleConditionField(
                                key,
                                'value',
                                e.target.value
                            )
                        }
                        inputProps={{
                            max,
                            min,
                            style: { textAlign: 'center' }
                        }}
                    />
                </Grid>
            </>
        );
    };

    renderPicker = (key, value, picker) => {
        const pickerProps = {
            allValue: 'All',
            allValueId: picker.allValue,
            multiple: picker.multiple,
            onChange: (v) => this.handleConditionField(key, 'value', v),
            value
        };

        if (picker.type === 'Lifecycle') {
            return <LifecyclePicker {...pickerProps} />;
        }
        if (picker.type === 'Tag') {
            return <TagsPicker {...pickerProps} includeGlobalTags={false} />;
        }

        return <RiskPicker {...pickerProps} />;
    };

    renderPickerCondition = (key) => {
        const { conditions } = this.state;
        const condition = conditions[key];
        const { value, picker } = condition;

        if (Helpers.isEmpty(picker)) {
            return null;
        }

        return (
            <>
                <Grid item xs={2}>
                    {renderEqualOperand()}
                </Grid>
                <Grid item xs={5}>
                    {this.renderPicker(key, value, picker)}
                </Grid>
            </>
        );
    };

    renderCondition = (key) => {
        const { conditions } = this.state;
        const condition = conditions[key];
        const { name, number, hidden, picker } = condition;

        if (hidden) {
            return null;
        }

        let width = 12;
        if (number || picker) {
            width = 5;
        }

        return (
            <Grid
                container
                spacing={3}
                key={`condition_${key}`}
                alignItems="flex-end"
            >
                <Grid item xs={width}>
                    <TextField
                        fullWidth
                        disabled
                        value={name}
                        inputProps={{
                            style: { textAlign: 'center' }
                        }}
                    />
                </Grid>
                {number && this.renderNumberCondition(key)}
                {picker && this.renderPickerCondition(key)}
            </Grid>
        );
    };

    renderConditions = () => {
        const { t } = this.props;
        const { conditions } = this.state;

        if (Helpers.isEmpty(conditions)) {
            return null;
        }

        return (
            <Grid item xs={12}>
                <Typography color="textSecondary" fontWeight={600}>
                    {t('Common.conditions')}
                </Typography>
                {Object.keys(conditions).map((key) =>
                    this.renderCondition(key)
                )}
            </Grid>
        );
    };

    renderMethodSelect = () => {
        const { t, methods } = this.props;
        const { method } = this.state;

        return (
            <Grid item xs={12}>
                <Typography color="textSecondary" fontWeight={600}>
                    {t('Alert.delivery_method')}
                </Typography>
                <Select
                    IconComponent={ExpandMore}
                    variant="standard"
                    value={method}
                    onChange={(e) => this.handleMethod(e.target.value)}
                    style={{ width: '100%' }}
                >
                    {Object.keys(methods).map((key) => {
                        let value = key;
                        let text = methods[key];

                        if (key === 'webhook') {
                            value = false;
                            text = (
                                <Box
                                    sx={{
                                        color: 'text.disabled',
                                        cursor: 'not-allowed'
                                    }}
                                >
                                    {text} (Coming Soon)
                                </Box>
                            );
                        }

                        return (
                            <MenuItem key={key} value={value}>
                                {text}
                            </MenuItem>
                        );
                    })}
                </Select>
            </Grid>
        );
    };

    renderDestinations = () => {
        const { t, methods } = this.props;
        const { destinations, method } = this.state;
        const label = methods[method];

        return (
            <Grid item xs={12}>
                <Typography color="textSecondary" fontWeight={600}>
                    {t('Common.destinations')}
                </Typography>
                <MultiInput
                    expanded
                    fields={[{ field: 'destination', label }]}
                    max={5}
                    min={0}
                    onChange={(p) => {
                        this.setState({
                            destinations: p.rows.map((val) => ({
                                destination: val.destination
                            }))
                        });
                    }}
                    placeholder="support@auvik.com"
                    sx={{ paddingLeft: '5px' }}
                    values={destinations}
                    zeroText={`Add ${label}`}
                />
            </Grid>
        );
    };

    render() {
        const { t, handleChangeView, partner } = this.props;
        const { loaded } = this.state;

        if (!loaded) {
            return null;
        }

        return (
            <Grid item xs={12} style={{ padding: '10px' }}>
                <Grid container spacing={3}>
                    {partner && this.renderClientSelect()}
                    {this.renderEventSelect()}
                    {this.renderConditions()}
                    {this.renderMethodSelect()}
                    {this.renderDestinations()}

                    <Grid item xs={12} display="flex" justifyContent="flex-end">
                        <Button
                            data-trackid="alerts_create_cancel"
                            variant="outlined"
                            color="primary"
                            onClick={() => handleChangeView('configurations')}
                        >
                            {t('Common.cancel')}
                        </Button>
                        <Button
                            data-trackid="alerts_create_create"
                            color="primary"
                            variant="contained"
                            onClick={() => this.save()}
                            style={{ marginLeft: '4px' }}
                        >
                            {t('Common.create')}
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        );
    }
}
export default withRouter(withTranslation()(Create));
